import React from "react";
import styled from "styled-components";
import { IComponent } from "../../Component";

export const IFRAME_COMPONENT = "iframe";

export type IframeAspectRatio = "16:9" | "4:3" | "1:1" | "custom";

export interface IframeSpec {
  type: typeof IFRAME_COMPONENT;
  url: string;
  aspectRatio: IframeAspectRatio;
  width: string;
  height: string;
  allowFullscreen: boolean;
  lazy: boolean;
  title: string;
  showBorder: boolean;
}

const getAspectRatioPadding = (ratio: IframeAspectRatio): string => {
  switch (ratio) {
    case "16:9":
      return "56.25%"; // 9/16 = 0.5625
    case "4:3":
      return "75%"; // 3/4 = 0.75
    case "1:1":
      return "100%";
    case "custom":
      return "0";
    default:
      return "56.25%";
  }
};

const IframeWrapper = styled.div<{
  aspectRatio: IframeAspectRatio;
  width: string;
  height: string;
  showBorder: boolean;
}>`
  width: ${(props) => props.width || "100%"};
  ${(props) =>
    props.aspectRatio === "custom"
      ? `height: ${props.height || "500px"};`
      : ""};
  position: relative;
  border: ${(props) => (props.showBorder ? "1px solid #ddd" : "none")};
  box-shadow: ${(props) =>
    props.showBorder ? "0 2px 4px rgba(0,0,0,0.1)" : "none"};
  border-radius: ${(props) => (props.showBorder ? "4px" : "0")};
  overflow: hidden;
  background-color: #f8f9fa;

  ${(props) =>
    props.aspectRatio !== "custom"
      ? `
    padding-top: ${getAspectRatioPadding(props.aspectRatio)};
  `
      : ""}
`;

const ResponsiveIframe = styled.iframe`
  position: ${(props) => props.style?.position || "absolute"};
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  border: 0;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  width: 100%;
  background-color: #f8f9fa;
  color: #6c757d;
  border: 1px dashed #dee2e6;
  border-radius: 4px;
  font-size: 16px;
`;

const Component = (props: { spec: IframeSpec }) => {
  const {
    url,
    aspectRatio,
    width,
    height,
    allowFullscreen,
    lazy,
    title,
    showBorder,
  } = props.spec;

  if (!url) {
    return <EmptyState>Keine URL angegeben</EmptyState>;
  }

  const iframeProps = {
    src: url,
    title: title || "Embedded content",
    loading: lazy ? "lazy" : "eager",
    allowFullScreen: allowFullscreen,
    referrerPolicy: "no-referrer-when-downgrade" as const,
    style: {
      position: aspectRatio === "custom" ? "relative" : "absolute",
    },
  };

  return (
    <IframeWrapper
      aspectRatio={aspectRatio}
      width={width}
      height={height}
      showBorder={showBorder}
    >
      <ResponsiveIframe {...iframeProps} />
    </IframeWrapper>
  );
};

export const IframeComponent: IComponent<IframeSpec> = {
  component: Component,
};
